<template>
  <div class="flex gap-2">
    <VaIconMenuCollapsed
      class="cursor-pointer"
      :class="{ 'x-flip': !isSidebarMinimized }"
      :color="collapseIconColor"
      @click="isSidebarMinimized = !isSidebarMinimized"
    />

    <nav class="flex items-center">
      <VaBreadcrumbs>
        <VaBreadcrumbsItem label="Home" :to="{ name: 'dashboard' }" />
        <VaBreadcrumbsItem v-for="item in items" :key="item.label" :label="item.label" :to="getBreadcrumbTo(item)" />
      </VaBreadcrumbs>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useColors } from 'vuestic-ui'

import { Nav } from '../../router'
import { useGlobalStore } from '../../stores/global-store'
import VaIconMenuCollapsed from '../icons/VaIconMenuCollapsed.vue'

const { isSidebarMinimized } = storeToRefs(useGlobalStore())
const { t } = useI18n()

const route = useRoute()

const NavigationRoutes = inject<Nav>('nav-routes')!

type BreadcrumbNavigationItem = {
  label: string
  to: string
  hasChildren: boolean
}

const findRouteName = (name: string) => {
  const traverse = (routers: any[]): string => {
    for (const router of routers) {
      if (router.name === name) {
        return router.displayName
      }
      if (router.children) {
        const result = traverse(router.children)
        if (result) {
          return result
        }
      }
    }
    return ''
  }

  return traverse(NavigationRoutes.routes)
}

const items = computed(() => {
  const result: { label: string; to: string; hasChildren: boolean }[] = []
  if (!route?.matched) {
    return result
  }
  route.matched.forEach((route) => {
    const labelKey = findRouteName(route.name as string)
    if (!labelKey) {
      return
    }
    result.push({
      label: t(labelKey),
      to: route.path,
      hasChildren: route.children && route.children.length > 0,
    })
  })
  return result
})

const { getColor } = useColors()

const collapseIconColor = computed(() => getColor('secondary'))

const getBreadcrumbTo = (item: BreadcrumbNavigationItem) => {
  if (item.to === '/tenant-admin') {
    return { name: 'tenant-settings' }
  }
  if (!item.hasChildren) {
    return item.to
  }

  return undefined
}
</script>

<style lang="scss" scoped>
.x-flip {
  transform: scaleX(-100%);
}
</style>
